import { alpha, IconButton, Paper, styled, Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material';
import DataObjectRounded from '@mui/icons-material/DataObjectRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import RouteRounded from '@mui/icons-material/RouteRounded';

import { isDarkMode } from '@localstack/services';
import { NewBadge } from '@localstack/ui';

export const NUDGE_BULLETS = [
  { Icon: RouteRounded, text: 'Trace calls across services' },
  { Icon: DataObjectRounded, text: 'Inspect full request and response payloads' },
  { Icon: ErrorOutlineRounded, text: 'Catch errors and missing IAM permissions' },
];

export const MOTION_DURATION = { enter: 180, exit: 130 };
export const MOTION_EASING = {
  enter: 'cubic-bezier(0.32, 0.72, 0, 1)',
  exit: 'cubic-bezier(0.4, 0, 1, 1)',
};
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const REDUCED_MOTION_MEDIA_QUERY = `@media ${REDUCED_MOTION_QUERY}`;

export const NUDGE_ID = 'app-inspector-nudge';

const CENTERED_ROW_STYLES = {
  display: 'flex',
  alignItems: 'center',
} as const;
const squareSize = (size: number | string) => ({ width: size, height: size });

export const Anchor = styled('span')({
  position: 'relative',
  display: 'inline-flex',
});

// Persists independently of the card until the user visits App Inspector.
export const Dot = styled('span')(({ theme }) => {
  const accentColor = theme.palette.primary.main;

  return {
    ...squareSize(theme.spacing(1)),
    position: 'absolute',
    top: theme.spacing(-0.5),
    right: theme.spacing(-0.5),
    borderRadius: '50%',
    backgroundColor: accentColor,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    pointerEvents: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: -3,
      border: `1px solid ${alpha(accentColor, 0.65)}`,
      borderRadius: 'inherit',
      animation: `appInspectorNudgePulse 1.6s ${MOTION_EASING.enter} 400ms infinite`,
    },
    '@keyframes appInspectorNudgePulse': {
      '0%': { opacity: 0.75, transform: 'scale(0.72)' },
      '100%': { opacity: 0, transform: 'scale(1.7)' },
    },
    [REDUCED_MOTION_MEDIA_QUERY]: {
      '&::after': {
        animation: 'none',
      },
    },
  };
});

const CARD_WIDTH = 46;

// Non-modal by design so it does not intercept clicks on the page behind it.
export const Card = styled(Paper)(({ theme }) => {
  const dark = isDarkMode(theme);
  const shadowColor = theme.palette.common.black;

  return {
    width: theme.spacing(CARD_WIDTH),
    maxWidth: `calc(100vw - ${theme.spacing(3)})`,
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 2.25, 2.25),
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${dark ? theme.palette.divider : alpha(shadowColor, 0.08)}`,
    backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.045)} 0%, transparent 96px)`,
    boxShadow: dark
      ? `0 2px 4px ${alpha(shadowColor, 0.3)}, 0 14px 32px ${alpha(
          shadowColor,
          0.34,
        )}, 0 28px 56px ${alpha(shadowColor, 0.22)}`
      : `0 2px 4px ${alpha(shadowColor, 0.06)}, 0 14px 32px ${alpha(
          shadowColor,
          0.12,
        )}, 0 28px 56px ${alpha(shadowColor, 0.08)}`,
  };
});

export const DismissButton = styled(IconButton)(({ theme }) => ({
  ...squareSize(40),
  marginInlineEnd: theme.spacing(-0.75),
  color: theme.palette.text.secondary,
  transition: `color 150ms ease, background-color 150ms ease, transform 150ms ${MOTION_EASING.enter}`,
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
  '&:active': {
    transform: 'scale(0.96)',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
  [REDUCED_MOTION_MEDIA_QUERY]: {
    transition: 'none',
  },
}));

export const CardHeader = styled('div')(({ theme }) => ({
  ...CENTERED_ROW_STYLES,
  minHeight: theme.spacing(4),
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

export const HeaderContent = styled('div')(({ theme }) => ({
  ...CENTERED_ROW_STYLES,
  minWidth: 0,
  flex: 1,
  gap: theme.spacing(1),
}));

export const ProductBadge = styled(NewBadge)(({ theme }) => {
  const dark = isDarkMode(theme);
  const accent = theme.palette.primary;

  return {
    flexShrink: 0,
    borderColor: alpha(accent.main, dark ? 0.7 : 0.28),
    backgroundColor: alpha(accent.main, dark ? 0.45 : 0.12),
    color: dark ? theme.palette.common.white : accent.dark,
    boxShadow: dark
      ? `0 0 0 1px ${alpha(accent.main, 0.1)}, 0 6px 14px ${alpha(theme.palette.common.black, 0.2)}`
      : `0 4px 10px ${alpha(accent.dark, 0.1)}`,
  };
});

export const CardTitle = styled(Typography)({
  minWidth: 0,
  fontWeight: 700,
  textWrap: 'balance',
});

export const MotionFrame = styled('div')({
  transformOrigin: 'top center',
  willChange: 'opacity, transform',
  '&[data-placement^="top"]': {
    transformOrigin: 'bottom center',
  },
  '&[data-motion="enter"]': {
    animation: `appInspectorNudgeEnter ${MOTION_DURATION.enter}ms ${MOTION_EASING.enter} both`,
  },
  '&[data-motion="exit"]': {
    animation: `appInspectorNudgeExit ${MOTION_DURATION.exit}ms ${MOTION_EASING.exit} both`,
  },
  '@keyframes appInspectorNudgeEnter': {
    from: { transform: 'scale(0.97)' },
    to: { transform: 'scale(1)' },
  },
  '@keyframes appInspectorNudgeExit': {
    from: { transform: 'scale(1)' },
    to: { transform: 'scale(0.98)' },
  },
  [REDUCED_MOTION_MEDIA_QUERY]: {
    animation: 'none !important',
  },
});

export const BulletIcon = styled('span')(({ theme }) => {
  const accentColor = theme.palette.primary.main;

  return {
    ...CENTERED_ROW_STYLES,
    ...squareSize(theme.spacing(3)),
    flexShrink: 0,
    justifyContent: 'center',
    color: accentColor,
    backgroundColor: alpha(accentColor, isDarkMode(theme) ? 0.16 : 0.08),
    borderRadius: theme.spacing(0.75),
    '& svg': {
      fontSize: 16,
    },
  };
});

export const BulletList = styled('ul')(({ theme }) => ({
  margin: 0,
  marginTop: theme.spacing(1.75),
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  '& li': {
    ...CENTERED_ROW_STYLES,
    gap: theme.spacing(1),
    minHeight: theme.spacing(3),
  },
}));

const SmallCopy = styled(Typography)({
  fontSize: 13,
});

export const Description = styled(SmallCopy)(({ theme }) => ({
  marginTop: theme.spacing(0.75),
  color: theme.palette.text.secondary,
  lineHeight: 1.55,
  textWrap: 'pretty',
}));

export const Bullet = styled(SmallCopy)<TypographyProps<'li'>>(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  lineHeight: 1.35,
}));
